import { PlanConfirmationModal as PlanConfirmationModalComponent, ProcessingScreen } from '@components'

import { Space, Text, useModal, withModalConditionalRender } from '@billy/design-system'
import qs from 'qs'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from 'react-query'
import { useLocation } from 'react-use'

import { BackToSchoolCampaign } from '@features/backToSchoolCampaign/elements/BackToSchoolCampaign'
import { useBackToSchoolCampaign } from '@features/backToSchoolCampaign/hooks/useBackToSchoolCampaign'
import { SubscriptionPeriod } from '@modules-deprecated/app/organization/enums/subscriptionPeriod'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { ModalId } from '../../enums/modalId'
import { QueryKeys } from '../../enums/queryKeys'
import { isValidSubscriptionPlan } from '../../utils/isValidSubscriptionPlan'
import { useCouponEligibility } from '../settings/hooks/useCouponEligibility'
import { useCouponParams } from '../settings/hooks/useCouponParams'
import { SubscriptionPlans } from '../settings/routes/OrganizationSubscriptionSettings/elements/SubscriptionPlans'
import { fetchSubscription } from '../settings/routes/OrganizationSubscriptionSettings/services/query-api'
import { EditSubscriptionParams } from '../settings/routes/OrganizationSubscriptionSettings/types'
import {
  notifySubscriptionUpdateError,
  notifySubscriptionUpdateSuccess,
} from '../settings/routes/OrganizationSubscriptionSettings/utils'
import * as Styled from './styles'

const PlanConfirmationModal = withModalConditionalRender(PlanConfirmationModalComponent)

interface SubscriptionTrialExpiredProps {
  onEditSubscription: (detail: EditSubscriptionParams) => void
  organizationId: string
}

export const SubscriptionTrialExpired = ({ onEditSubscription, organizationId }: SubscriptionTrialExpiredProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [showProcessingScreen, setShowProcessingScreen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>(SubscriptionPlan.Premium)
  const [selectedPeriod, setSelectedPeriod] = useState<SubscriptionPeriod>(SubscriptionPeriod.Yearly)
  const [confirmPrice, setConfirmPrice] = useState<number>(0)
  const [recurringPrice, setRecurringPrice] = useState<number>(0)
  const [shouldShowBackToSchoolOffer, setShouldShowBackToSchoolOffer] = useState(true)

  const { audience, isLoading: isCampaignLoading, remainingCampaignDays } = useBackToSchoolCampaign()

  const { data: subscription, isLoading: loadingSubscription } = useQuery(
    [QueryKeys.OrganizationSubscription, organizationId],
    () => fetchSubscription(organizationId),
    {
      enabled: !!organizationId,
    },
  )
  const { search } = useLocation()

  const params = qs.parse(search || '', {
    ignoreQueryPrefix: true,
  })

  const { handleCouponEligibilityCheck, setDiscountPercent, totalPrice, totalRecurringPrice } = useCouponEligibility({
    organizationId,
    selectedPeriod,
    selectedPlan,
    confirmPrice,
    recurringPrice,
    signupCouponPlan: isValidSubscriptionPlan(params.plan) ? params.plan : undefined,
  })

  const { open: openPlanConfirmationModal, close: closeConfirmationModal } = useModal(ModalId.PlanConfirmationModal, {
    onClose: () => setDiscountPercent(0),
  })

  const handlePlanConfirm = (coupon?: string) => {
    setShowProcessingScreen(true)
    onEditSubscription({
      detail: {
        plan: selectedPlan,
        period: selectedPeriod || undefined,
        price: confirmPrice,
        coupon,
        callback: (error) => {
          queryClient.invalidateQueries(QueryKeys.OrganizationSubscription)
          queryClient.invalidateQueries(QueryKeys.OrganizationSubscriptionInfo)
          closeConfirmationModal()

          if (error) {
            setShowProcessingScreen(false)
            notifySubscriptionUpdateError(t, error.message)
          } else {
            // Keep the processing screen up: the org stays locked until Ember's polling picks up
            // the unlock and navigates away (up to ~5s).
            notifySubscriptionUpdateSuccess(t)
          }
        },
      },
    })
  }

  const handlePlanSelect = useCallback(
    (plan: SubscriptionPlan, period?: SubscriptionPeriod) => {
      if (period) {
        setSelectedPeriod(period)
      }
      setSelectedPlan(plan)
      openPlanConfirmationModal()
    },
    [openPlanConfirmationModal],
  )

  useCouponParams({ params, organizationId, handlePlanSelect })

  const handlePriceChange = useCallback((confirmPrice: number, recurringPrice: number) => {
    setConfirmPrice(confirmPrice)
    setRecurringPrice(recurringPrice)
  }, [])

  const handleViewMonthlyPlansClick = useCallback(() => {
    setShouldShowBackToSchoolOffer(false)
  }, [])

  if (loadingSubscription || !subscription || isCampaignLoading) {
    return null
  }

  if (audience && shouldShowBackToSchoolOffer) {
    return (
      <Styled.CampaignOverlay>
        <BackToSchoolCampaign
          audience={audience}
          onEditSubscription={onEditSubscription}
          onViewMonthlyPlans={handleViewMonthlyPlansClick}
          remainingCampaignDays={remainingCampaignDays}
        />
      </Styled.CampaignOverlay>
    )
  }

  return (
    <>
      <Text variant="bodyLG">{t('organization_subscription.trial.subheader')}</Text>
      <Space size="s40" />
      <Styled.PlansWrapper>
        <SubscriptionPlans onPlanSelect={handlePlanSelect} />
      </Styled.PlansWrapper>
      {showProcessingScreen && <ProcessingScreen />}
      <PlanConfirmationModal
        id={ModalId.PlanConfirmationModal}
        onConfirm={handlePlanConfirm}
        onCouponEligibilityCheck={(coupon) => handleCouponEligibilityCheck(coupon)}
        onPriceChange={handlePriceChange}
        planText={t(`organization_subscription.${selectedPlan}`)}
        price={totalPrice}
        recurringPrice={totalRecurringPrice}
        subscriptionPeriod={selectedPeriod}
        subscriptionPlan={selectedPlan}
      />
    </>
  )
}
